import { useState, useEffect } from 'react';
import {
	Modal, Box, Typography, IconButton, InputAdornment, TextField, Select, MenuItem, FormControl,
	InputLabel, Button, Paper, CircularProgress, Fade, Divider, Grid2 as Grid, Avatar,
	Alert
} from '@mui/material';
import {
	Close as CloseIcon,
	Search as SearchIcon,
	InfoOutlined
} from '@mui/icons-material';
import { fetchWrapper, fetchWrapperNoAuth, SERVER } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import SearchedProductsTable from '../Tables/SearchedProductsTable';
import ErrorModal from './ErrorModal';

function isNumeric(str) {
	return /^[0-9]+$/.test(str);
}

const SearchCatalogModal = ({
	open,
	onClose,
	setCurrProduct,
	prefetchItemData,
	handleNext,
	isReplen
}) => {
	const { theme } = useTheme();
	const { user, password, encodedAuth } = useAuth();

	const [showInfo, setShowInfo] = useState(true);
	const [identifierType, setIdentifierType] = useState("barcode");
	const [searchValue, setSearchValue] = useState('');
	const [searchedItems, setSearchedItems] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [openErrorModal, setOpenErrorModal] = useState(false);

	const [barcode, setBarcode] = useState('');
	const [itemId, setItemId] = useState('');
	const [pName, setPName] = useState('');
	const [sku, setSku] = useState('');

	useEffect(() => {
		if (open) {
			if (isReplen) {
				setIdentifierType(localStorage.getItem("ReplenLastSearch") || "sku");
			} else {
				setIdentifierType("barcode");
			}
		}

	}, [open, isReplen])

	useEffect(() => {
		if (!open) {
			setSearchValue('');
			setSearchedItems([]);
			setBarcode('');
			setItemId('');
			setPName('');
			setSku('');
		}
	}, [open]);

	const alertError = (msg) => {
		setErrorMessage(msg);
		setOpenErrorModal(true);
	}

	const handleInfoClose = () => {
		setShowInfo(false);
	};

	const handleSearch = async (productId) => {
		if (!productId) {
			alertError("Enter Valid Value to see results");
			setSearchedItems([]);
			return;
		}
		setSearchLoading(true);
		let uniqeItems = [];
		let tempIdentifierTypeCheck = identifierType;

		if (isReplen) {
			if (identifierType == 'itemId') {
				try {
					const items = await fetchWrapper(`${SERVER}/falcon_get_skus_by_item_id`, user, password, encodedAuth, {
						method: "POST",
						body: {
							item_id: productId
						}
					});
					setSearchedItems(items);
					setSearchLoading(false);
				} catch (error) {
					console.error('Error:', error);
					setSearchedItems([]);
				} finally {
					setSearchLoading(false);
				}
			} else {
				try {
					const items = await fetchWrapper(`${SERVER}/falcon_get_skus_by_query`, user, password, encodedAuth, {
						method: "POST",
						body: {
							type: identifierType,
							query: productId
						}
					});
					setSearchedItems(items);
					setSearchLoading(false);
				} catch (error) {
					console.error('Error:', error);
					setSearchedItems([]);
				} finally {
					setSearchLoading(false);
				}
			}
		} else {
			if (identifierType === "barcode") {
				if (productId.length < 11) {
					tempIdentifierTypeCheck = "itemId";
					setBarcode("");
					setItemId(productId);
				}
				if (!isNumeric(productId)) {
					tempIdentifierTypeCheck = "pName";
					setBarcode("");
					setPName(productId);
				}
			}
			setIdentifierType(tempIdentifierTypeCheck);

			let tempIdentifierType = "upc";
			if (tempIdentifierTypeCheck === "itemId" || tempIdentifierTypeCheck === "pName") {
				tempIdentifierType = "query";
			} else if (tempIdentifierTypeCheck === "barcode") {
				try {
					if (productId.length < 12) {
						productId = productId.padStart(12, '0');
					}
					const barcodePrds = await fetchWrapperNoAuth(`${SERVER}/get_options_from_barcode/${productId}`, user, password);
					uniqeItems = barcodePrds;
				} catch (error) {
					console.error('Error:', error.message);
					setSearchedItems([]);
				}
				tempIdentifierType = productId.length === 12 ? "upc" : "gtin";
			} else {
				try {
					const itemData = await fetchWrapper(`${SERVER}/post_catalog_search_sku/${productId}`, user, password, encodedAuth);
					setSearchedItems([itemData]);
					setSearchLoading(false);
					prefetchItemData && prefetchItemData(itemData);
					return;
				} catch (error) {
					console.error('Error:', error);
					setSearchedItems([]);
					setSearchLoading(false);
					return;
				}
			}

			const url = `${SERVER}/get_item_search`;
			const body = {
				[tempIdentifierType]: productId
			};

			try {
				const items = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
				const concatItems = [...uniqeItems, ...items].reduce((acc, current) => {
					if (!acc.some(item => item.itemId === current.itemId)) {
						acc.push(current);
					}
					return acc;
				}, []);
				setSearchedItems(concatItems);
				setSearchLoading(false);

				if (prefetchItemData) {
					concatItems.slice(0, 5).forEach(item => prefetchItemData(item));
				}
			} catch (error) {
				console.error('Error:', error);
				setSearchedItems(uniqeItems);
				setSearchLoading(false);
				if (prefetchItemData) {
					uniqeItems.slice(0, 5).forEach(item => prefetchItemData(item));
				}
			}
		}
	};

	const handleSearchSubmit = () => {
		if (searchValue.trim()) {
			handleSearch(searchValue.trim());
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter') {
			handleSearchSubmit();
		}
	};

	const handlePaste = (e) => {
		setTimeout(() => {
			const pastedValue = e.target.value;
			setSearchValue(pastedValue);
			if (pastedValue.trim()) {
				handleSearch(pastedValue.trim());
			}
		}, 0);
	};

	const getCurrentSearchValue = () => {
		switch (identifierType) {
			case 'barcode': return barcode;
			case 'itemId': return itemId;
			case 'pName': return pName;
			case 'sku': return sku;
			case 'upc': return sku;
			case 'gtin': return sku;
			default: return searchValue;
		}
	};

	const setCurrentSearchValue = (value) => {
		setSearchValue(value);
		switch (identifierType) {
			case 'barcode': setBarcode(value); break;
			case 'itemId': setItemId(value); break;
			case 'pName': setPName(value); break;
			case 'sku': setSku(value); break;
			case 'upc': setSku(value); break;
			case 'gtin': setSku(value); break;
		}
	};

	const getIdentifierLabel = (type) => {
		switch (type) {
			case 'barcode': return 'Barcode';
			case 'pName': return 'Product Name';
			case 'itemId': return 'Item ID';
			case 'upc': return 'UPC';
			case 'gtin': return 'GTIN';
			case 'sku': return 'SKU';
			default: return 'Search';
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			closeAfterTransition
		>
			<Fade in={open}>
				<Paper
					elevation={24}
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: { xs: '95vw', sm: '90vw', md: '80vw', lg: '70vw' },
						maxWidth: '1100px',
						maxHeight: '90vh',
						backgroundColor: theme.background.tertiary,
						borderRadius: 3,
						boxShadow: `0 20px 60px rgba(0, 0, 0, 0.3)`,
						overflow: 'hidden',
						fontFamily: theme.fontFamily,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							p: 3,
							background: `linear-gradient(135deg, ${theme.color.primary} 0%, ${theme.color.secondary} 100%)`,
							color: theme.text.negative,
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Avatar
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.2)',
									color: theme.text.negative,
								}}
							>
								<SearchIcon />
							</Avatar>
							<Typography
								variant="h5"
								sx={{
									fontWeight: 600,
									fontFamily: theme.fontFamily,
								}}
							>
								{isReplen ? "Replen" : "Search"} Products
							</Typography>
							{isReplen && showInfo && (
								<Alert
									severity="error"
									variant="contained"
									sx={{
										fontFamily: theme.fontFamily,
										backgroundColor: `${theme.accent.error}66`,
										borderColor: theme.accent.error,
										color: theme.text.primary,
									}}
									action={
										<IconButton
											size="small"
											aria-label="close"
											onClick={handleInfoClose}
											sx={{
												color: theme.accent.error,
											}}
										>
											<CloseIcon fontSize="small" />
										</IconButton>
									}
									icon={<InfoOutlined sx={{ color: theme.accent.error }} />}
								>
									<Typography variant="body2"
										sx={{
											fontFamily: theme.fontFamily,
											color: theme.text.negative,
										}}>
										Ensure that your item is WFS-eligible
									</Typography>
								</Alert>
							)}

						</Box>
						<IconButton
							onClick={onClose}
							sx={{
								color: theme.text.negative,
								backgroundColor: 'rgba(255, 255, 255, 0.1)',
								'&:hover': {
									backgroundColor: 'rgba(255, 255, 255, 0.2)',
								},
							}}
						>
							<CloseIcon />
						</IconButton>
					</Box>

					<Box sx={{ p: 3 }}>
						<Grid container spacing={2} alignItems="center">
							<Grid item size={{ xs: 12, md: 3 }}>
								<FormControl fullWidth variant="outlined">
									<InputLabel
										sx={{
											color: theme.text.secondary,
											fontFamily: theme.fontFamily,
										}}
									>
										Search Type
									</InputLabel>
									<Select
										value={identifierType}
										label="Search Type"
										onChange={(e) => {
											setIdentifierType(e.target.value);
											if (isReplen) {
												localStorage.setItem("ReplenLastSearch", e.target.value)
											}
										}}
										sx={{
											fontFamily: theme.fontFamily,
											color: theme.text.primary,
											'& .MuiOutlinedInput-notchedOutline': {
												borderColor: theme.border.medium,
											},
											'&:hover .MuiOutlinedInput-notchedOutline': {
												borderColor: theme.color.primary,
											},
											'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
												borderColor: theme.color.primary,
											},
										}}
									>
										{!isReplen && (
											<MenuItem value="barcode">
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: theme.fontFamily }}>
													Barcode
												</Box>
											</MenuItem>
										)}
										{!isReplen && (
											<MenuItem value="pName">
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: theme.fontFamily }}>
													Product Name
												</Box>
											</MenuItem>
										)}
										<MenuItem value="itemId">
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: theme.fontFamily }}>
												Item ID
											</Box>
										</MenuItem>
										{isReplen && (
											<MenuItem value="upc">
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: theme.fontFamily }}>
													UPC
												</Box>
											</MenuItem>
										)}
										{isReplen && (
											<MenuItem value="gtin">
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: theme.fontFamily }}>
													GTIN
												</Box>
											</MenuItem>
										)}
										<MenuItem value="sku">
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: theme.fontFamily }}>
												SKU
											</Box>
										</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item size={{ xs: 12, md: 9 }}>
								<TextField
									fullWidth
									variant="outlined"
									label={getIdentifierLabel(identifierType)}
									value={getCurrentSearchValue()}
									onChange={(e) => setCurrentSearchValue(e.target.value)}
									onKeyDown={handleKeyPress}
									onPaste={handlePaste}
									autoFocus
									sx={{
										fontFamily: theme.fontFamily,
										'& .MuiOutlinedInput-root': {
											'& fieldset': {
												borderColor: theme.border.medium,
											},
											'&:hover fieldset': {
												borderColor: theme.color.primary,
											},
											'&.Mui-focused fieldset': {
												borderColor: theme.color.primary,
											},
										},
										'& .MuiInputLabel-root': {
											color: theme.text.secondary,
											fontFamily: theme.fontFamily,
										},
										'& .MuiInputBase-input': {
											color: theme.text.primary,
											fontFamily: theme.fontFamily,
										},
									}}
									slotProps={{
										input: {
											startAdornment: identifierType === 'barcode' && (
												<InputAdornment position="start">
													<Typography
														sx={{
															color: getCurrentSearchValue() ? theme.text.primary : theme.accent.error,
															fontFamily: theme.fontFamily,
														}}
													>
														#
													</Typography>
												</InputAdornment>
											),
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														onClick={handleSearchSubmit}
														disabled={!getCurrentSearchValue().trim()}
														sx={{
															color: getCurrentSearchValue() ? theme.color.primary : theme.accent.disabled,
															'&:hover': {
																backgroundColor: `${theme.color.primary}20`,
															},
														}}
													>
														<SearchIcon />
													</IconButton>
												</InputAdornment>
											),
										}
									}}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider sx={{ borderColor: theme.border.medium }} />

					<Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
						{searchLoading ? (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									py: 6,
								}}
							>
								<CircularProgress
									size={48}
									sx={{
										color: theme.color.primary,
										mb: 2,
									}}
								/>
								<Typography
									variant="body1"
									sx={{
										color: theme.text.secondary,
										fontFamily: theme.fontFamily,
									}}
								>
									Searching products...
								</Typography>
							</Box>
						) : searchedItems.length > 0 ? (
							<Box sx={{ width: "100%" }}>
								<SearchedProductsTable
									products={searchedItems}
									setCurrProduct={setCurrProduct}
									isLoading={searchLoading}
									handleNext={async (product) => {
										onClose();
										await handleNext(product);
									}}
								/>
							</Box>
						) : (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									py: 6,
								}}
							>
								<Avatar
									sx={{
										backgroundColor: `${theme.text.tertiary}20`,
										color: theme.text.tertiary,
										width: 64,
										height: 64,
										mb: 2,
									}}
								>
									<SearchIcon fontSize="large" />
								</Avatar>
								<Typography
									variant="h6"
									sx={{
										color: theme.text.secondary,
										fontFamily: theme.fontFamily,
										mb: 1,
									}}
								>
									No products found
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: theme.text.tertiary,
										fontFamily: theme.fontFamily,
										textAlign: 'center',
									}}
								>
									Try searching with a different identifier type or value
								</Typography>
							</Box>
						)}
					</Box>

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: 2,
							p: 1,
							borderTop: `1px solid ${theme.border.medium}`,
							backgroundColor: theme.background.secondary,
						}}
					>
						<Button
							variant="outlined"
							onClick={onClose}
							sx={{
								color: theme.text.secondary,
								borderColor: theme.border.medium,
								fontFamily: theme.fontFamily,
								'&:hover': {
									borderColor: theme.color.primary,
									backgroundColor: `${theme.color.primary}10`,
								},
							}}
						>
							Cancel
						</Button>
					</Box>
					<ErrorModal open={openErrorModal} setOpen={setOpenErrorModal} message={errorMessage} />
				</Paper>
			</Fade>
		</Modal >
	);
};

export default SearchCatalogModal;